import { Box, Grid, Button, CircularProgress, Theme, useMediaQuery, useTheme } from '@mui/material';
import { filterWaitingListItems } from '@repo/widget-utils/cart/cartUtils';
import { validateContactPerson } from '@repo/widget-utils/checkout-info-helper';
import { postWaitingListItem } from '@repo/widget-utils/services/fetchers/product';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { createRef, useEffect, useState } from 'react';
import { useAtom } from 'ximple';
import { useSelector } from 'src/hooks';
import { cartAtom, selectCartComposition, selectWaitingListItems } from 'src/state/cart/cartAtom';
import { showError } from 'src/utils/widget/error-handling';
import { WaitingListSuccessView } from 'src/components/domain/waiting-list/WaitingListSuccessView';
import CheckoutInfoView from 'src/components/domain/checkout-info/CheckoutInfoView';
import { useLocale } from '@repo/i18n';
import { CheckoutInfo, CartItem, ProductInstance, Iso2Code } from '@repo/types';
import { countryOptionsEn } from '@repo/common-utils/countries';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import { route } from 'src/components/common/router/Router';
import Overlay from 'src/components/common/overlay/Overlay';
import Title from 'src/components/common/timeslots-typography/Title';
import BackButton from 'src/components/domain/checkout/BackButton';
import { initializeHiddenBodyStyle, useHiddenBody } from 'src/hooks/common/ui/useHiddenBody';
import { clearWaitingListItemsEvent } from 'src/state/cart/cart.reducer';
import CheckoutSummary, { useSummaryFeatures } from '../checkout/CheckoutSummary';

const hiddenBodyStyle = initializeHiddenBodyStyle('activities');

export function WaitingListCheckout() {
    const { t } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();
    const [checkoutInfo] = useAtom(checkoutInfoAtom);
    const [cartItems] = useAtom(cartAtom);
    const waitingListItems = useSelector(cartItems, selectWaitingListItems);
    const cartComposition = useSelector(cartItems, selectCartComposition);
    const sentinelRef = createRef<HTMLDivElement>();
    const largeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [clickedSubmit, setClickedSubmit] = useState(false);

    const showShare = false;
    const enableCartClearing = true;
    const enableItemEditing = false;
    const isWaitingListMode = true;

    const summaryFeatures = useSummaryFeatures(
        showShare,
        enableCartClearing,
        enableItemEditing,
        null,
        isWaitingListMode,
    );

    const submitToWaitingList = () => {
        handleSubmitToWaitingList({
            t,
            checkoutInfo,
            cartItems,
            setIsSubmitting,
            setSubmitted,
            setClickedSubmit,
        });
    };

    useHiddenBody(hiddenBodyStyle);

    useEffect(() => {
        sentinelRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (cartComposition === 'empty' || cartComposition === 'regularOnly') {
            route('/checkout');
        }
    }, [cartComposition]);

    if (submitted) {
        return (
            <Overlay>
                <div ref={sentinelRef} tabIndex={-1} style={{ outline: 'none' }}>
                    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                        <WaitingListSuccessView
                            cartItems={waitingListItems}
                            onClose={() => {
                                cartAtom.update(clearWaitingListItemsEvent());
                                route('/');
                            }}
                        />
                    </Box>
                </div>
            </Overlay>
        );
    }

    return (
        <Overlay>
            <div ref={sentinelRef} tabIndex={-1} style={{ outline: 'none' }}>
                <Box
                    sx={{
                        position: 'relative',
                        backgroundColor: customizations.checkoutHeaderColor,
                        height: theme.spacing(8),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                />
                <Box position="relative" m="0 auto" maxWidth={`calc(1100px + ${theme.spacing(2)})`}>
                    <Box
                        sx={{
                            px: theme.spacing(1),
                            mt: theme.spacing(4),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: '1280px',
                                maxWidth: '100%',
                                margin: '0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Grid container justifyContent={largeScreen ? 'flex-start' : 'center'}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                        mb: 1,
                                    }}
                                >
                                    <BackButton
                                        label={t.backToWebsite}
                                        onClick={() => route('/')}
                                    />
                                </Box>
                                <Title
                                    text={t.waiting_list}
                                    description={t.please_fill_in_all_required_information}
                                />
                            </Grid>

                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="flex-start"
                                direction="row"
                                spacing={3}
                            >
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <CheckoutInfoView
                                        showErrors={clickedSubmit}
                                        showAddressFields={false}
                                        showInfoQuestionnaires={false}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 5 }}>
                                    <CheckoutSummary
                                        hideGoPay={true}
                                        headingText={t.waiting_list}
                                        cartItems={waitingListItems}
                                        summaryFeatures={summaryFeatures}
                                    />

                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => {
                                                submitToWaitingList();
                                            }}
                                            disabled={isSubmitting || !checkoutInfo}
                                            startIcon={
                                                isSubmitting ? (
                                                    <CircularProgress size={20} />
                                                ) : undefined
                                            }
                                            sx={{ minWidth: 200 }}
                                        >
                                            {isSubmitting ? t.processing : t.join_waiting_list}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Overlay>
    );
}

async function handleSubmitToWaitingList({
    t,
    checkoutInfo,
    cartItems,
    setIsSubmitting,
    setSubmitted,
    setClickedSubmit,
}: {
    t: ReturnType<typeof useLocale>['t'];
    checkoutInfo: CheckoutInfo | null;
    cartItems: CartItem[];
    setIsSubmitting: (value: boolean) => void;
    setSubmitted: (value: boolean) => void;
    setClickedSubmit: (value: boolean) => void;
}) {
    if (!checkoutInfo) {
        showError(t.please_fill_in_all_required_information);
        return;
    }

    const { contactPerson, companyInfo, contactType } = checkoutInfo;

    let customerName = '';
    if (contactType === 'company' && companyInfo) {
        customerName = `${companyInfo.companyName} (${contactPerson.firstName} ${contactPerson.lastName})`;
    } else {
        customerName = `${contactPerson.firstName} ${contactPerson.lastName}`;
    }

    const { isValid: isContactPersonValid } = validateContactPerson(contactPerson, false);
    if (!customerName || !isContactPersonValid) {
        setClickedSubmit(true);
        showError(t.notAllFieldsHaveBeenFilledPleaseFillAllFields);
        return;
    }

    setIsSubmitting(true);

    try {
        const waitingListItemsByPlan: Record<
            string,
            {
                participants: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    plan_price_id: number;
                    quantity: number;
                }[];
            }
        > = {};

        const waitingListItems = filterWaitingListItems(cartItems);

        waitingListItems.forEach((item: CartItem) => {
            item.products.forEach((instance: ProductInstance) => {
                const tourId = instance.id;

                if (!waitingListItemsByPlan[tourId]) {
                    waitingListItemsByPlan[tourId] = {
                        participants: [],
                    };
                }

                item.ticketOptions.forEach((option) => {
                    if (option.quantity > 0) {
                        waitingListItemsByPlan[tourId].participants.push({
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            plan_price_id: parseInt(option.id.toString(), 10),
                            quantity: option.quantity,
                        });
                    }
                });
            });
        });

        if (Object.keys(waitingListItemsByPlan).length === 0) {
            throw new Error(t.no_waiting_list_items_found);
        }

        let notes = '';
        if (contactPerson.address) {
            notes = `Address: ${contactPerson.address}`;
            if (contactPerson.postCode) notes += `, ${contactPerson.postCode}`;
            if (contactPerson.city) notes += `, ${contactPerson.city}`;
            if (contactPerson.country) notes += `, ${contactPerson.country}`;
        }

        const submissions = Object.values(waitingListItemsByPlan).map(
            async (waitingListItem, index) =>
                postWaitingListItem({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    project_id: parseInt(Object.keys(waitingListItemsByPlan)[index], 10),
                    name: customerName,
                    email: contactPerson.email,
                    phone: `+${contactPerson.phone.dialCode}${contactPerson.phone.number}`,
                    country: countryOptionsEn[contactPerson.country as Iso2Code],
                    notes: notes || undefined,
                    prices: waitingListItem.participants.map((p) => ({
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        price_id: p.plan_price_id,
                        quantity: p.quantity,
                    })),
                }),
        );

        await Promise.all(submissions);
        setSubmitted(true);
    } catch (error) {
        showError(
            t.an_error_occurred_when_submitting_your_request +
                ': ' +
                (error instanceof Error ? error.message : t.problemLoadingData),
        );
    } finally {
        setIsSubmitting(false);
    }
}
